import styled from '@emotion/styled';
import classNames from 'classnames';
import React, { TdHTMLAttributes } from 'react';
import { semantic_colors, text_styles } from '../../foundation';
import { buildCSSWithLength, getZIndex } from '../../styles';
import { CellSize, ColumnAlign } from '../table/types';
import { cell_size_css } from './styles';

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  id?: string;
  fixed?: boolean;
  fixedColumnWidth?: number;
  empty?: boolean;
  disabled?: boolean;
  align?: ColumnAlign;
  resizable?: boolean;
  colSpan?: number;
  lastRow?: boolean;
  hasDisabledStyle?: boolean;
  clickable?: boolean;
  selectable?: boolean;
  lastColumn?: boolean;
  /** @default 'medium' */
  size?: CellSize;
  /** @default true */
  truncate?: boolean;
  /** @default true */
  wrapContent?: boolean;
}

export const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  (
    {
      children,
      className,
      colSpan,
      id,
      valign = 'middle',
      size = 'medium',
      truncate = true,
      wrapContent,
      width,
      ...props
    },
    ref,
  ) => {
    return (
      <StyledTableCell
        ref={ref}
        className={classNames(id ? `pds-table-cell-${id}` : 'pds-table-cell', className)}
        colSpan={colSpan}
        valign={valign}
        size={size}
        truncate={truncate}
        wrapContent={wrapContent}
        width={width}
        aria-disabled={props.disabled}
        {...props}
      >
        {children}
      </StyledTableCell>
    );
  },
);

const StyledTableCell = styled.td<{
  fixed?: boolean;
  fixedColumnWidth?: number;
  empty?: boolean;
  disabled?: boolean;
  align?: ColumnAlign;
  resizable?: boolean;
  lastRow?: boolean;
  lastColumn?: boolean;
  clickable?: boolean;
  selectable?: boolean;
  hasDisabledStyle?: boolean;
  valign: TableCellProps['valign'];
  size: CellSize;
  truncate?: boolean;
  wrapper?: boolean;
  wrapContent?: boolean;
  width?: string | number;
}>`
  vertical-align: ${({ valign }) => valign};
  ${text_styles.Body_13_Regular};
  text-align: ${({ align }) => align ?? 'left'};
  ${({ disabled, hasDisabledStyle }) =>
    disabled && hasDisabledStyle
      ? `
          color: ${semantic_colors.content.disabled};
          > * {
            opacity: 0.4;
            color: ${semantic_colors.content.primary};
          }
  `
      : `
    color: ${semantic_colors.content.primary};
  `};
  ${({ empty, size }) => (empty ? 0 : cell_size_css[size])};
  border-bottom: 1px solid ${semantic_colors.border.primary};
  ${({ fixed, fixedColumnWidth }) =>
    fixed &&
    `
     z-index: ${getZIndex('tableFixedCol')};
     position: sticky;
     ${buildCSSWithLength('left', fixedColumnWidth ?? 0)};
     background-color: ${semantic_colors.background.surface};`}
  ${({ resizable }) =>
    resizable &&
    `.ContentsWrapper {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }`}
     ${({ width, truncate }) =>
    width && truncate
      ? `
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: ${width}px;
        white-space: nowrap;
    `
      : ``}
  ${({ wrapContent }) => (wrapContent ? 'white-space: normal; word-break: break-word;' : '')};
  ${({ lastRow }) => lastRow && 'border-bottom: none;'};
  ${({ lastColumn }) => lastColumn && 'border-right: none;'};
  ${({ clickable, selectable, disabled }) =>
    disabled ? `cursor: not-allowed;` : clickable && `cursor: ${selectable ? 'pointer' : 'not-allowed'}`}

  &.pds-table-expanded-cell {
    background-color: ${semantic_colors.fill.tertiary};
  }
  &.pds-table-divider-cell {
    background-color: ${semantic_colors.background.base};
    padding: 2px;
  }
`;
